import React from 'react';
import { Box, Skeleton } from '@mui/material';
import {
  LEADS_BG_ELEVATED,
  LEADS_PANEL_BORDER,
  LEADS_SKELETON_MUTED,
  LEADS_SKELETON_PRIMARY,
  LEADS_UNSEEN_BADGE_BG,
} from './leadsUi.styles';

/**
 * Skeleton del índice de Leads: misma estructura que `LeadCard` (LeadsPanel.js).
 * Mismo contenedor (radio 8, fondo elevado, borde izquierdo 3px, px 12 / py 10,
 * gap 4px), mismas alturas de línea (nombre 18px, metas 16px), chip de
 * consultas nuevas al lado del nombre, punto de nivel a la derecha y el menú
 * ⋮ en su posición absoluta. Paleta de skeleton de Registros / Listas.
 *
 * Si cambia `LeadCard`, actualizar esto (docs/frontend/loading-states.md).
 */

const skPrimarySx = { bgcolor: LEADS_SKELETON_PRIMARY, borderRadius: '4px' };
const skMutedSx = { bgcolor: LEADS_SKELETON_MUTED, borderRadius: '4px' };
const skAccentSx = { bgcolor: LEADS_UNSEEN_BADGE_BG, borderRadius: '10px' };

const cardSx = {
  position: 'relative',
  borderRadius: '8px',
  bgcolor: LEADS_BG_ELEVATED,
  borderLeft: `3px solid ${LEADS_SKELETON_PRIMARY}`,
  px: '12px',
  py: '10px',
  display: 'flex',
  flexDirection: 'column',
  gap: '4px',
};

/**
 * Línea con el alto del texto real (minHeight: la fila del nombre crece a 20px
 * cuando lleva el chip, igual que en la card); el bloque gris va centrado.
 */
function Line({ height, children, sx = null }) {
  return (
    <Box sx={{ minHeight: height, display: 'flex', alignItems: 'center', minWidth: 0, ...(sx || {}) }}>
      {children}
    </Box>
  );
}

const NAME_WIDTHS = ['46%', '34%', '42%', '30%', '38%'];
const CONTACT_WIDTHS = ['62%', '54%', '70%', '48%', '58%'];
const INTEREST_WIDTHS = ['40%', '52%', '36%', '44%', '48%'];
const META_WIDTHS = ['72%', '64%', '78%', '60%', '68%'];

/**
 * @param {{ count?: number, withMenu?: boolean }} props
 *   `withMenu`: la card real muestra ⋮ solo si se puede archivar (espacio propio).
 */
export default function LeadCardsSkeleton({ count = 5, withMenu = true }) {
  return (
    <Box
      sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}
      aria-busy="true"
      aria-label="Cargando leads"
      data-testid="leads-skeleton"
    >
      {Array.from({ length: count }).map((_, idx) => {
        const i = idx % NAME_WIDTHS.length;
        return (
          <Box key={idx} sx={cardSx} data-testid="lead-card-skeleton">
            <Line height={18} sx={{ gap: 1, pr: withMenu ? 3 : 0 }}>
              <Skeleton variant="rounded" height={14} sx={{ ...skPrimarySx, width: NAME_WIDTHS[i], maxWidth: '50%' }} />
              {idx % 2 === 0 && (
                <Skeleton variant="rounded" width={idx % 4 === 0 ? 104 : 92} height={20} sx={{ ...skAccentSx, flexShrink: 0 }} />
              )}
              <Box sx={{ flex: 1 }} />
              <Skeleton variant="circular" width={8} height={8} sx={{ bgcolor: LEADS_PANEL_BORDER, flexShrink: 0 }} />
            </Line>
            <Line height={16}>
              <Skeleton variant="rounded" height={11} sx={{ ...skMutedSx, width: CONTACT_WIDTHS[i] }} />
            </Line>
            {idx % 3 !== 2 && (
              <Line height={16}>
                <Skeleton variant="rounded" height={11} sx={{ ...skMutedSx, width: INTEREST_WIDTHS[i] }} />
              </Line>
            )}
            <Line height={16}>
              <Skeleton variant="rounded" height={10} sx={{ ...skMutedSx, width: META_WIDTHS[i] }} />
            </Line>
            {withMenu && (
              <Skeleton
                variant="circular"
                data-testid="lead-card-skeleton-menu"
                width={18}
                height={18}
                sx={{ position: 'absolute', top: 9, right: 7, bgcolor: LEADS_SKELETON_MUTED }}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
}
